import React from 'react';
import { IconButton as MuiIconButton, IconButtonProps as MuiIconButtonProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { Icon } from '../Icon';
import type { IconProps } from '../Icon';
import type { IconType } from '../../theme/icons';

export type IconButtonSize = 'small' | 'medium' | 'large' | 'xlarge';

export type IconButtonVariant =
  | 'primary'
  | 'secondary'
  | 'link'
  | 'outline'
  | 'ghost'
  | 'destructive';

export interface IconButtonProps
  extends Omit<MuiIconButtonProps, 'size' | keyof PounceStyleProps>, PounceStyleProps {
  /** The size of the icon button, defaults to large */
  size?: IconButtonSize;

  /** The text associated with the icon button */
  'aria-label'?: string;

  /** The icon present on the button  */
  icon: IconType;

  /** The style of the icon button */
  variant?: IconButtonVariant;

  /** The border style of the button  */
  borderStyle?: 'square' | 'circle';

  /** Whether the button is disabled */
  disabled?: boolean;

  /** Whether the button should have a loading spinner next to it */
  loading?: boolean;

  /** Whether the button should always be marked as active. Helpful for menus. */
  active?: boolean;
}

/**
 * IconButton component built on top of MUI IconButton.
 *
 * A wrapper that makes an Icon component clickable. Provides all the variants
 * and functionality from the original Pounce IconButton while leveraging MUI's
 * accessibility and interaction patterns.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    borderStyle = 'square',
    active = false,
    variant = 'primary',
    size = 'large',
    icon,
    loading = false,
    disabled = false,
    sx,
    ...rest
  },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  const isDisabled = disabled || loading;

  // Map pounce sizes to icon sizes
  const getIconSize = (): IconProps['size'] => {
    if (size === 'small') {
      return 'small';
    }
    if (size === 'medium') {
      return 'medium';
    }
    // xlarge and large both use large icon
    return 'large';
  };
  const iconSize = getIconSize();

  // Combine pounce-generated styles with button-specific styles and user sx
  const combinedSx: MuiIconButtonProps['sx'] = {
    borderRadius: borderStyle === 'circle' ? '50%' : '8px',
    ...pounceStyles,
    // Apply loading styles when loading is true
    ...(loading && {
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to right, currentColor 50%, transparent 50%)',
        backgroundSize: '200% 100%',
        animation: 'pounce-loading 4s linear infinite reverse',
        opacity: 0.3,
        borderRadius: 'inherit',
      },
      '@keyframes pounce-loading': {
        '0%': {
          backgroundPosition: '-200% 0',
        },
        '100%': {
          backgroundPosition: '200% 0',
        },
      },
    }),
    ...(sx || {}),
  } as MuiIconButtonProps['sx'];

  const iconButtonProps = {
    ref,
    variant,
    size,
    disabled: isDisabled,
    'aria-pressed': active,
    'data-active': active || undefined,
    sx: combinedSx,
    ...otherProps,
  } as any;

  return (
    <MuiIconButton {...iconButtonProps}>
      <Icon type={icon} size={iconSize} />
    </MuiIconButton>
  );
});
